import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import { CourseTabs } from '../course-tabs';
import { useCheckIncludeProfiles } from '@/hooks/useAuthorityLevel';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import { Filter, FilterContent, FilterInput, FilterSelectNative } from '@/components/custom/filter';
import { EmptyState } from '@/components/custom/empty-state';
import { CourseCertificateModal } from './course-certificate-modal';
import { t } from 'i18next';
import { Loader } from '@/components/custom/loader';
import { FiPrinter } from 'react-icons/fi';
import toast from 'react-hot-toast';
import moreMedal from '@/assets/images/more-medal.svg';
import courseMedal from '@/assets/images/course-medal.svg';
import { DashboardContent } from '@/layouts/dashboard';
import { TiaBanner } from '@/components/custom/tia-banner';
import tiaCursos from '@/assets/images/tia/TIA-CURSOS.png';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableUser, TableUserContent, TableUserTitle } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Medal, MedalImg, MedalValue } from '@/components/custom/medal';

export function CourseCertificate(props) {
  const { uri } = props;
  const playfulStudent = usePlayfulStudent();
  const isStudent = useCheckIncludeProfiles({ only: ['student'] });
  const [relationships, setRelationships] = useState({
    courses: []
  });
  const [showModal, setShowModal] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [currentCertificate, setCurrentCertificate] = useState();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const client = useClient();

  const filters = useFormik({
    initialValues: {
      course: 'blank',
      type: 'blank'
    }
  });

  const options = [
    { name: 'Curso', id: 1 },
    { name: 'Módulo', id: 2 }
  ];

  const filterBySearch = certificates.filter(
    item => searchTerm === '' || item?.['learning-system']?.title?.toLowerCase().includes(searchTerm?.toLowerCase()) || item?.['learning-system']?.course?.title?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  const filterByType = filterBySearch.filter(item => {
    if (filters.values.type === 'blank') {
      return true;
    }

    if (filters.values.type == 1) {
      return item?.['certificate-type'] === 'Curso';
    }

    if (filters.values.type == 2) {
      return item?.['certificate-type'] === 'Módulo';
    }
  });

  const certificatesFilteredByCourse = filterByType.filter(item => filters.values.course === 'blank' || item?.['learning-system']?.course?.id == filters.values.course);

  const getRelationships = async () => {
    const { data: courses } = await client.fetch('courses');
    setRelationships({
      courses: courses || []
    });
  };

  const getCertificates = async () => {
    setLoading(true);
    const { data, error } = await client.fetch('user_certificates');
    if (error) {
      toast.error(t('toast.errorGetCertifications'));
    } else {
      setCertificates(data);
    }
    setLoading(false);
  };

  const downloadCertificate = async item => {
    window.open(`${process.env.REACT_APP_DEV_URL}//user_certificates/download/${item['hash-id']}.pdf`, '_blank');
  };

  useEffect(() => {
    getRelationships();
    getCertificates();
  }, []);

  if (useCheckIncludeProfiles({ only: ['admin', 'designer', 'student'] }) === false) return <Error404 />;

  const orderedCertificates = certificatesFilteredByCourse.sort((a, b) => {
    const dateA = new Date(b['created-at'].split('/').reverse().join('/'));
    const dateB = new Date(a['created-at'].split('/').reverse().join('/'));
    return dateA - dateB;
  });

  const breadcrumbs = {
    title: 'Cursos e Aulas',
    nav: [
      {
        route: uri,
        name: 'Certificados',
        isActive: true
      }
    ]
  };

  const filterContent = (
    <Filter>
      <FilterContent>
        <FilterInput
          label="Buscar"
          placeholder={t('textsCommon.research')}
          type="search"
          name="search"
          id="search"
          onChange={e => setSearchTerm(e.target.value)}
        />

        <FilterSelectNative
          blankLabel={t('filter.blankLabelAll2')}
          label={'Selecione um tipo'}
          value={filters.values.type}
          onChange={e => filters.setFieldValue('type', e.target.value)}
          options={options}
        />

        <FilterSelectNative
          label={t('exams.selectCourse')}
          value={filters.values.course}
          onChange={e => filters.setFieldValue('course', e.target.value)}
          options={relationships.courses.map(o => ({
            id: o.id,
            name: o.title
          }))}
        />
      </FilterContent>
    </Filter>
  );

  return (
    <DashboardContent breadcrumbs={breadcrumbs}>
      {playfulStudent && (
        <TiaBanner
          className="mb-6 mt-6 md:mt-20"
          image={tiaCursos}
          imageHeight={185}
          imageOffset={180}
          bubbleMaxWidth={800}
          text="Aqui ficam seus certificados! 🏆 Baixe, imprima e mostre pra todo mundo o que você já conquistou!"
        />
      )}

      {(playfulStudent || isStudent) && (
        <div className={playfulStudent ? 'mb-6 overflow-x-auto' : undefined}>
          <CourseTabs selectedTab="certificados" />
        </div>
      )}

      {filterContent}

      {!loading && (
        <>
          {orderedCertificates.length > 0 && !loading && (
            <Table className="min-w-[730px]">
              <TableHeader>
                <TableRow>
                  <TableHead
                    scope="col"
                    className="w-[72px]"
                  />
                  <TableHead
                    scope="col"
                    className="w-[100px]"
                  />
                  <TableHead scope="col">Curso/Módulo</TableHead>
                  <TableHead
                    scope="col"
                    className="w-[120px]"
                  >
                    {t('lms.tableHeaderConcludedIn')}
                  </TableHead>
                  <TableHead
                    scope="col"
                    className="w-[130px]"
                  />
                  <TableHead
                    scope="col"
                    className="w-[62px]"
                  />
                </TableRow>
              </TableHeader>

              <TableBody>
                {orderedCertificates.map((item, index) => {
                  return (
                    <TableRow key={index}>
                      <TableHead scope="row">
                        <Medal
                          size="sm"
                          className="text-sm"
                        >
                          <MedalImg
                            src={item?.['certificate-type'] === 'Módulo' ? moreMedal : courseMedal}
                            alt="nova medalha"
                          />
                          <MedalValue>+1</MedalValue>
                        </Medal>
                      </TableHead>
                      <TableCell>
                        <Badge
                          variant="bright"
                          size="xs"
                        >
                          {item?.['certificate-type']}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <TableUser>
                          <TableUserContent>
                            <TableUserTitle>{item?.['learning-system']?.title}</TableUserTitle>
                          </TableUserContent>
                        </TableUser>
                      </TableCell>
                      <TableCell>{item?.['created-at']}</TableCell>
                      <TableCell>
                        <Button
                          variant="link"
                          className="p-0"
                          onClick={() => {
                            setCurrentCertificate(item);
                            setShowModal(true);
                          }}
                        >
                          Detalhamento
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="icon"
                          title={t('lms.printCertificate')}
                          onClick={() => {
                            downloadCertificate(item);
                          }}
                        >
                          <ButtonIcon>
                            <FiPrinter />
                          </ButtonIcon>
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {orderedCertificates.length === 0 && !loading && (
            <EmptyState
              type="content"
              title={t('emptyState.titleCertificate')}
              text={t('emptyState.textCertificate')}
            />
          )}
        </>
      )}

      {loading && <Loader />}

      <CourseCertificateModal
        show={showModal}
        onClose={() => setShowModal(false)}
        currentCertificate={currentCertificate}
      />
    </DashboardContent>
  );
}
